// Multi-page PDF renderer that lets the user drop pins on any page. Each page
// renders onto a canvas via pdf.js; a transparent overlay above the canvas
// captures clicks when pin-drop mode is active and renders existing pin markers
// at normalized coordinates (0-1 relative to the page viewport).

import { useEffect, useRef, useState } from "react";

export default function PdfPinViewer({
  url,
  pinComments,          // comments with pin_x/pin_y/pin_page
  pinNumberById,        // Map(comment.id -> ordinal)
  pinDraft,             // { page, x, y } | null
  placingPin,
  onPageClick,          // (pageNumber, x, y) => void
  onPinClick,           // (commentId) => void
  activeCommentId,
}) {
  const [pages, setPages] = useState([]); // [{ pageNumber, width, height }]
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        // Point to a public CDN worker that matches the installed version.
        pdfjsLib.GlobalWorkerOptions.workerSrc =
          `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
        const loadingTask = pdfjsLib.getDocument({ url, withCredentials: false });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        const out = [];
        for (let p = 1; p <= pdf.numPages; p++) {
          const page = await pdf.getPage(p);
          // Target width ~1200 CSS px for sharpness without blowing up memory.
          const unscaled = page.getViewport({ scale: 1 });
          const targetW = 1200;
          const scale = targetW / unscaled.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          out.push({
            pageNumber: p,
            dataUrl: canvas.toDataURL("image/jpeg", 0.85),
            w: viewport.width,
            h: viewport.height,
          });
          if (cancelled) return;
          setPages([...out]); // progressive reveal
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to render PDF");
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  const handleClick = (pageNumber) => (e) => {
    if (!placingPin) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    onPageClick(pageNumber, x, y);
  };

  if (error) {
    return (
      <div className="p-8 text-center text-sm text-[var(--gc-danger)]" data-testid="pdf-render-error">
        Couldn't render PDF: {error}
      </div>
    );
  }
  if (pages.length === 0) {
    return <div className="text-[var(--gc-text-muted)] py-16 text-center font-mono-tech text-sm">RENDERING PDF…</div>;
  }

  return (
    <div ref={containerRef} className="w-full space-y-4 p-2" data-testid="pdf-pin-viewer">
      {pages.map((p) => {
        const pagePins = pinComments.filter((c) => (c.pin_page || 1) === p.pageNumber);
        const draftOnPage = pinDraft && pinDraft.page === p.pageNumber ? pinDraft : null;
        return (
          <div key={p.pageNumber} className="relative shadow-sm border border-[var(--gc-border)]" data-testid={`pdf-page-${p.pageNumber}`}>
            <div className="absolute left-2 top-2 z-10 chip chip-navy font-mono-tech text-[10px]" style={{ pointerEvents: "none" }}>
              p. {p.pageNumber}
            </div>
            <div
              className="relative w-full"
              style={{ cursor: placingPin ? "crosshair" : "default" }}
              onClick={handleClick(p.pageNumber)}
            >
              <img src={p.dataUrl} alt={`page ${p.pageNumber}`} className="w-full block bg-white" />
              {pagePins.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onPinClick(c.id); }}
                  style={{ left: `${c.pin_x * 100}%`, top: `${c.pin_y * 100}%` }}
                  className={`absolute -translate-x-1/2 -translate-y-full ${activeCommentId === c.id ? "scale-110" : ""}`}
                  data-testid={`pin-marker-${c.id}`}
                >
                  <span className="bg-[var(--gc-orange)] text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-lg border-2 border-white">
                    {pinNumberById.get(c.id)}
                  </span>
                </button>
              ))}
              {draftOnPage && (
                <div
                  style={{ left: `${draftOnPage.x * 100}%`, top: `${draftOnPage.y * 100}%` }}
                  className="absolute -translate-x-1/2 -translate-y-full"
                >
                  <span className="bg-[var(--gc-navy)] text-white w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ring-4 ring-[var(--gc-orange)]/40 border-2 border-white">
                    ?
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
